import { useState, useMemo, useEffect } from 'react';
import { Stack } from '@mui/material';
import AttestationStatusCharts from './attestation-status-charts';
import AttestationFilters from './attestation-filters';
import AttestationTable from './attestation-table';

interface AttestationOverviewProps {
  data: {
    users: any[];
    systems: any[];
    departments: any[];
    cloudAccounts: any[];
    resources: any[];
    resourceGroups: any[];
    rejectedResources: any[];
  };
  system?: string;
  department?: string;
  resourceOwner?: string;
  cloudAccount?: string;
}

function AttestationOverview({ data, system, department, resourceOwner, cloudAccount }: Readonly<AttestationOverviewProps>) {
  const [filters, setFilters] = useState({
    cloudAccount: '',
    resourceOwner: '',
    system: '',
    department: '',
  });

  const [groupBy, setGroupBy] = useState<'hierarchy' | 'systems'>('systems');

  const handleFiltersChange = (newFilters: {
    cloudAccount: string;
    resourceOwner: string;
    system: string;
    department: string;
  }) => {
    setFilters(newFilters);
  };

  const handleGroupByChange = (newGroupBy: 'hierarchy' | 'systems') => {
    setGroupBy(newGroupBy);
  };

  const filteredData = useMemo(() => {
    let filteredResources = data.resources;

    // cloud account
    if (filters.cloudAccount) {
      filteredResources = filteredResources.filter(
        (resource: any) =>
          resource.cloud_account_id?.toString() === filters.cloudAccount,
      );
    }

    // resource owner
    if (filters.resourceOwner) {
      filteredResources = filteredResources.filter((resource: any) => {
        const resourceOwnerId =
          resource.owner_id?.toString() ||
          resource.id_user_assigned_owner?.toString();
        return resourceOwnerId === filters.resourceOwner;
      });
    }

    // system
    if (filters.system) {
      filteredResources = filteredResources.filter((resource: any) => {
        const systemId = resource.system_id?.toString();
        return systemId === filters.system;
      });
    }

    // department
    if (filters.department) {
      filteredResources = filteredResources.filter((resource: any) => {
        const departmentId = resource.department_id?.toString();
        return departmentId === filters.department;
      });
    }

    return {
      ...data,
      resources: filteredResources,
    };
  }, [data, filters]);

  // On first focusScope, set the filters if not already set by the user
  useEffect(() => {
    if (system || department || resourceOwner || cloudAccount) {
      // Only set incoming filters the very first time (or when ALL are empty)
      if ((system || department || resourceOwner || cloudAccount)
        && Object.values(filters).every(v => !v)) {
        setFilters(prev => ({
          ...prev,
          ...(system ? { system } : {}),
          ...(department ? { department } : {}),
          ...(resourceOwner ? { resourceOwner } : {}),
          ...(cloudAccount ? { cloudAccount } : {}),
        }));
      }
    }
  }, [system, department, resourceOwner, cloudAccount, filters]);

  return (
    <Stack spacing={2}>
      <div data-attestation-filters-section>
        <AttestationFilters
          onFiltersChange={handleFiltersChange}
          data={data}
          loading={false}
          error={null}
          initialCloudAccount={cloudAccount || ''}
          initialResourceOwner={resourceOwner || ''}
          initialSystem={system || ''}
          initialDepartment={department || ''}
        />
      </div>

      <AttestationStatusCharts
        display="all"
        defaultChartScope="resource_owners"
        data={filteredData}
      />

      <AttestationTable
        groupBy={groupBy}
        onGroupByChange={handleGroupByChange}
        display="all"
        isReadonly={true}
        data={filteredData}
      />
    </Stack>
  );
}

export default AttestationOverview;
